import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators, ValidatorFn } from '@angular/forms';

import { Observable, of, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { promptLanguageOptions } from '../../../Common/Forms/data/prompt-language-options';
import { languageDataLocalizationMap } from '../../../Core/Localization/language-localization.model';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { CheckboxTypesEnum } from '../../../Common/Forms/enums/checkbox-types.enum';

import { ViMonitor } from '../../models/vi-monitor.model';
import { AnswerEngineOption, getAllEngineOptions } from '../../models/answer-engine-option.model';

@Component({
    selector: 'agpr-vi-monitor-settings-panel',
    templateUrl: './vi-monitor-settings-panel.component.html',
    styleUrls: ['./vi-monitor-settings-panel.component.scss'],
    standalone: false
})
export class ViMonitorSettingsPanelComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<any>;
    @Input() monitor: ViMonitor;
    @Input() existingQuestionCount: number = 0;


    // The language field uses forceSelection, and the underlying autocomplete clears itself when the
    // typed text doesn't match a suggestion (including having cleared it entirely). Track the last
    // confirmed selection here so we can restore it once the user leaves the field (see onLanguageBlur).
    private cachedLanguage: DropdownOptionModel | null = null;
    private languageOptions: DropdownOptionModel[] = [];

    confirmed: boolean = false;
    hideSlideOverlay: boolean = false;
    subscriptions: Subscription[] = [];
    languageOptions$: Observable<DropdownOptionModel[]> = of([]);

    settingsForm: FormGroup;

    readonly id: string = 'viMonitorSettingsPanel';
    readonly buttonTypes = ButtonTypesEnum;
    readonly severityEnum = messageSeverityEnum;
    readonly checkboxTypes = CheckboxTypesEnum;
    readonly answerEngineOptions: AnswerEngineOption[] = getAllEngineOptions();
    readonly splitOnPattern: RegExp = /(")?,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)\1 */g;

    constructor(
        public coreDialogService: CoreDialogService,
        private currentUserStateService: CurrentUserStateService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        super(coreDialogService, 'ViMonitorSettingsPanel');
        this.settingsForm = this.buildForm();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.settingsForm.controls['language'].valueChanges.subscribe(value => {
                if (value) {
                    this.cachedLanguage = value;
                }
            })
        );

        this.subscriptions.push(
            this.currentUserStateService.getUILanguageTag().pipe(take(1)).subscribe(uiTag => {
                this.languageOptions = promptLanguageOptions.map(option => new DropdownOptionModel(
                    languageDataLocalizationMap[`${uiTag}.${option.value}`],
                    option.value
                ));
                this.languageOptions$ = of(this.languageOptions);
                this.patchFromMonitor(uiTag);
            })
        );
    }

    onLanguageBlur(): void {
        requestAnimationFrame(() => {
            const languageControl = this.settingsForm.controls['language'];
            if (!languageControl.value && this.cachedLanguage) {
                languageControl.setValue(this.cachedLanguage);
            }
        });
    }

    done(): void {
        if (!this.settingsForm.valid) {
            return;
        }
        this.updateResults();
        this.confirmed = true;
        this.hideSlideOverlay = true;
    }

    cancel(): void {
        this.hideSlideOverlay = true;
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.closeDialog(this.confirmed);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }


    private buildForm(): FormGroup {
        return new FormGroup({
            answerEngines: new FormArray(
                this.answerEngineOptions.map(() => new FormControl<boolean>(true, { nonNullable: true })),
                { validators: [this.answerEnginesValidator()] }
            ),
            analysisFocus: new FormControl<string[]>([], { nonNullable: true, validators: [Validators.required] }),
            language: new FormControl<DropdownOptionModel | null>(null),
            isCompetitiveBenchmarking: new FormControl<boolean>(false, { nonNullable: true })
        });
    }

    private answerEnginesValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const values = (control as FormArray<FormControl<boolean>>).value as boolean[];
            return values.some(value => value) ? null : { required: true };
        };
    }

    private patchFromMonitor(uiTag: string): void {
        if (!this.monitor) {
            this.setDefaultLanguage(uiTag);
            return;
        }

        const selectedEngines = this.monitor.answerEngines?.length
            ? this.monitor.answerEngines
            : this.answerEngineOptions.map(option => option.type);

        this.settingsForm.patchValue({
            answerEngines: this.answerEngineOptions.map(option => selectedEngines.includes(option.type)),
            analysisFocus: [...(this.monitor.analysisFocus ?? [])],
            language: this.findLanguageOption(this.monitor.language),
            isCompetitiveBenchmarking: this.monitor.isCompetitiveBenchmarking ?? false
        });

        if (!this.monitor.language) {
            this.setDefaultLanguage(uiTag);
        }
    }

    /** Prefers an exact match for the user's own UI locale, then its bare language code, then US English */
    private setDefaultLanguage(uiTag: string): void {
        if (this.settingsForm.controls['language'].value) {
            return;
        }

        const defaultOption = this.findLanguageOption(uiTag)
            ?? this.findLanguageOption(uiTag?.split('-')[0])
            ?? this.findLanguageOption('en-US');

        if (defaultOption) {
            this.settingsForm.patchValue({ language: defaultOption });
        }
    }

    private findLanguageOption(code?: string | null): DropdownOptionModel | null {
        return this.languageOptions.find(option => option.value === code) ?? null;
    }

    private updateResults() {
        const selectedFlags: boolean[] = this.settingsForm.value.answerEngines;
        this.monitor.answerEngines = this.answerEngineOptions.filter((option, i) => selectedFlags[i]).map(option => option.type);
        this.monitor.analysisFocus = this.settingsForm.value.analysisFocus;
        this.monitor.language = this.settingsForm.value.language?.value ?? undefined;
        this.monitor.isCompetitiveBenchmarking = this.settingsForm.value.isCompetitiveBenchmarking;
    }
}
